/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const ROLE_OPTIONS = [
  { key: 'user',  label: 'Users',  default: true  },
  { key: 'agent', label: 'Agents', default: false },
  { key: 'admin', label: 'Admin',  default: false },
];

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const PAGE_SIZE = 50;

const ROLE_COLORS = {
  user:  { bg: 'rgba(37, 99, 235, 0.08)',  border: 'rgba(37, 99, 235, 0.3)',  color: '#1d4ed8' },
  agent: { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.3)', color: '#047857' },
  admin: { bg: 'rgba(17, 17, 17, 0.06)',   border: 'rgba(17, 17, 17, 0.2)',   color: '#111'    },
};

const AdminMessagesPanel = () => {
  const [startDate, setStartDate] = useState(toISODate(daysAgo(30)));
  const [endDate, setEndDate]     = useState(toISODate(new Date()));

  const [roles, setRoles] = useState(() => {
    const init = {};
    ROLE_OPTIONS.forEach(r => { init[r.key] = r.default; });
    return init;
  });

  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);

  const [messages, setMessages]   = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [expanded, setExpanded]   = useState(null); // message _id with full content shown

  const searchDebounce = useRef(null);

  const fetchMessages = useCallback(async (pageOverride) => {
    setLoading(true);
    setError('');

    try {
      const selectedRoles = Object.entries(roles)
        .filter(([, on]) => on)
        .map(([k]) => k);

      if (selectedRoles.length === 0) {
        setError('Select at least one sender role.');
        setLoading(false);
        return;
      }

      const token = AuthService.getToken();
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        senderRole: selectedRoles.join(','),
        page: pageOverride ?? page,
        limit: PAGE_SIZE,
      });
      if (search.trim()) params.set('search', search.trim());

      const res = await fetch(`${API_URL}/messages/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.error || `Request failed (${res.status})`);
      }

      setMessages(json.data || []);
      setPagination(json.pagination || null);
      setStats(json.stats || null);
    } catch (err) {
      setError(err.message || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, roles, search, page]);

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when page changes
  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Debounced search → reset to page 1 and refetch
  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      fetchMessages(1);
    }, 400);
  };

  const toggleRole = (key) => {
    setRoles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = (days) => {
    setStartDate(toISODate(daysAgo(days)));
    setEndDate(toISODate(new Date()));
  };

  const handleRefresh = () => {
    setPage(1);
    fetchMessages(1);
  };

  const fmt = (v) => {
    if (loading) return <span className="skeleton">000</span>;
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString();
  };

  const fmtTime = (iso) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  };

  const recipientLabel = (m) => {
    if (!m.recipients || m.recipients.length === 0) return '—';
    if (m.recipients.length === 1) return m.recipients[0]?.name || m.recipients[0]?.email || '—';
    return `${m.recipients[0]?.name || '—'} +${m.recipients.length - 1}`;
  };

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Messages — matches Control Center design system from UserMetricsPanel */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 16px;
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .field-label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .date-input,
        .admin-panel .search-input {
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          outline: none;
          font-family: inherit;
          min-width: 150px;
        }
        .admin-panel .search-input { min-width: 260px; }
        .admin-panel .date-input:focus,
        .admin-panel .search-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .admin-panel .btn {
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }

        .admin-panel .preset-chip {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }
        .admin-panel .preset-chip:hover { background: #f9fafb; color: #111; }

        .admin-panel .signal-chip {
          padding: 7px 14px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .admin-panel .signal-chip:hover { background: #f9fafb; color: #111; }
        .admin-panel .signal-chip.on {
          background: rgba(37, 99, 235, 0.08);
          border-color: rgba(37, 99, 235, 0.35);
          color: #1d4ed8;
        }
        .admin-panel .signal-chip .dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #e5e7eb;
        }
        .admin-panel .signal-chip.on .dot { background: #2563eb; }

        /* Top summary stat tiles */
        .admin-panel .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 720px) {
          .admin-panel .stat-grid { grid-template-columns: 1fr; }
        }
        .admin-panel .stat-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 12px;
          padding: 18px;
        }
        .admin-panel .stat-tile.primary {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(29, 78, 216, 0.04));
          border-color: rgba(37, 99, 235, 0.18);
        }
        .admin-panel .stat-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .admin-panel .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .admin-panel .stat-tile.primary .stat-value { color: #1d4ed8; }
        .admin-panel .stat-sub {
          font-size: 11px;
          color: #6b7280;
          margin-top: 6px;
        }

        /* Message rows */
        .admin-panel .msg-row {
          display: grid;
          grid-template-columns: 200px 200px 1fr 150px;
          gap: 14px;
          align-items: start;
          padding: 12px 0;
          border-bottom: 1px solid #f5f5f5;
        }
        .admin-panel .msg-row:last-child { border-bottom: none; }
        @media (max-width: 900px) {
          .admin-panel .msg-row { grid-template-columns: 1fr; gap: 6px; }
        }
        .admin-panel .msg-head-row {
          display: grid;
          grid-template-columns: 200px 200px 1fr 150px;
          gap: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f0f0f0;
        }
        @media (max-width: 900px) {
          .admin-panel .msg-head-row { display: none; }
        }
        .admin-panel .msg-head {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
        }

        .admin-panel .msg-person {
          font-size: 13px;
          font-weight: 600;
          color: #111;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-panel .msg-email {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .admin-panel .role-pill {
          display: inline-block;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-radius: 4px;
          padding: 2px 6px;
          margin-top: 4px;
          border: 1px solid;
        }

        .admin-panel .msg-content {
          font-size: 13px;
          color: #374151;
          line-height: 1.45;
          cursor: pointer;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .admin-panel .msg-content.expanded {
          display: block;
          -webkit-line-clamp: unset;
          white-space: pre-wrap;
        }
        .admin-panel .msg-time {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          text-align: right;
        }
        @media (max-width: 900px) {
          .admin-panel .msg-time { text-align: left; }
        }

        .admin-panel .empty-state {
          text-align: center;
          padding: 36px 0;
          font-size: 13px;
          color: #9ca3af;
          font-weight: 500;
        }

        /* Pagination */
        .admin-panel .pager {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 14px;
        }
        .admin-panel .pager-info {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 600;
        }

        .admin-panel .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #fafafa 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: skel 1.4s linear infinite;
          border-radius: 6px;
          color: transparent;
        }
        @keyframes skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{
          fontSize: '11px', fontWeight: '600',
          letterSpacing: '0.08em', color: '#888',
          textTransform: 'uppercase'
        }}>
          Messages
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* Controls */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Filters</h3>
          <button
            className="btn btn-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label className="field-label">Start date</label>
            <input
              type="date"
              className="date-input"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label">End date</label>
            <input
              type="date"
              className="date-input"
              value={endDate}
              min={startDate}
              max={toISODate(new Date())}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', paddingBottom: '1px' }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                className="preset-chip"
                onClick={() => applyPreset(p.days)}
                type="button"
              >
                Last {p.label}
              </button>
            ))}
          </div>
          <div>
            <label className="field-label">Search content</label>
            <input
              type="text"
              className="search-input"
              placeholder="Search message text…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="field-label">Sender role</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {ROLE_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`signal-chip ${roles[opt.key] ? 'on' : ''}`}
                onClick={() => toggleRole(opt.key)}
              >
                <span className="dot" />
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            Toggle which sender roles to include, then hit Refresh. Search is applied automatically as you type.
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Summary</h3>
          <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
            {new Date(startDate).toLocaleDateString()} – {new Date(endDate).toLocaleDateString()}
          </span>
        </div>

        <div className="stat-grid">
          <div className="stat-tile primary">
            <div className="stat-label">Messages Sent</div>
            <div className="stat-value">{fmt(stats?.totalMessages)}</div>
            <div className="stat-sub">Matching current filters</div>
          </div>

          <div className="stat-tile">
            <div className="stat-label">Distinct Senders</div>
            <div className="stat-value">{fmt(stats?.distinctSenders)}</div>
            <div className="stat-sub">Unique accounts that sent a message</div>
          </div>

          <div className="stat-tile">
            <div className="stat-label">Conversations</div>
            <div className="stat-value">{fmt(stats?.distinctConversations)}</div>
            <div className="stat-sub">Threads with activity in window</div>
          </div>
        </div>
      </div>

      {/* Message list */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Messages</h3>
          <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
            Newest first · click a message to expand
          </span>
        </div>

        <div className="msg-head-row">
          <div className="msg-head">From</div>
          <div className="msg-head">To</div>
          <div className="msg-head">Message</div>
          <div className="msg-head" style={{ textAlign: 'right' }}>Sent</div>
        </div>

        {!loading && messages.length === 0 && (
          <div className="empty-state">No messages match the current filters.</div>
        )}

        {messages.map(m => {
          const rc = ROLE_COLORS[m.senderRole] || ROLE_COLORS.user;
          return (
            <div key={m._id} className="msg-row">
              <div>
                <div className="msg-person">{m.sender?.name || '—'}</div>
                <div className="msg-email">{m.sender?.email || ''}</div>
                <span
                  className="role-pill"
                  style={{ background: rc.bg, borderColor: rc.border, color: rc.color }}
                >
                  {m.senderRole}
                </span>
              </div>

              <div>
                <div className="msg-person">{recipientLabel(m)}</div>
                {m.recipients?.length === 1 && (
                  <div className="msg-email">{m.recipients[0]?.email || ''}</div>
                )}
                {m.recipients?.length > 1 && (
                  <div className="msg-email">
                    {m.recipients.map(r => r?.name).filter(Boolean).join(', ')}
                  </div>
                )}
              </div>

              <div
                className={`msg-content ${expanded === m._id ? 'expanded' : ''}`}
                onClick={() => setExpanded(expanded === m._id ? null : m._id)}
                title={expanded === m._id ? 'Click to collapse' : 'Click to expand'}
              >
                {m.content}
              </div>

              <div className="msg-time">{fmtTime(m.createdAt)}</div>
            </div>
          );
        })}

        {pagination && pagination.totalPages > 1 && (
          <div className="pager">
            <button
              className="btn"
              disabled={loading || page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className="pager-info">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total.toLocaleString()} messages
            </span>
            <button
              className="btn"
              disabled={loading || page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessagesPanel;